/**
 * Is this request coming from inside the native app shell?
 *
 * WHY THIS EXISTS. `isAppSurfaceRoute` answers "is this an account page", and
 * that is the right question for the web: marketing tags stay off /my-account
 * and on everywhere else. But the native shell wraps the *whole* site, so its
 * users meet /login — which is not an account route — and get the full
 * marketing treatment: consent banner, tag manager, the lot. In a webview that
 * banner reads as the app redirecting you somewhere on every cold start.
 *
 * The route cannot answer it, because /login is genuinely a marketing-reachable
 * page for web visitors and its consent banner is load-bearing there. What
 * differs is the CLIENT, so the client says so.
 *
 * THE MARKER. The shell appends a token to its User-Agent. That is the
 * conventional way an embedded webview identifies itself, it survives
 * redirects and client-side navigation, and it needs no cooperation from the
 * page being loaded.
 *
 * It is NOT a security boundary — a User-Agent is trivially forged. It governs
 * only which third-party scripts load, so the worst a forger achieves is
 * fewer tags on their own page view. Never gate data or authorisation on it.
 */

/** Appended by the shell; see ksebe-mobile/src/TabWebView.tsx. */
export const APP_SHELL_UA_TOKEN = 'ksebe-app'

export const isAppShellUserAgent = (
  user_agent: string | undefined | null,
): boolean => !!user_agent && user_agent.includes(APP_SHELL_UA_TOKEN)

/**
 * Should third-party marketing scripts be withheld from this page view?
 *
 * True when EITHER the route is the signed-in account surface (the existing
 * rule, unchanged for web visitors) OR the request came from the app shell
 * (every route, because the shell has no marketing surface at all).
 */
export const shouldWithholdMarketingScripts = (opts: {
  is_app_surface_route: boolean
  is_app_shell: boolean
}): boolean => opts.is_app_surface_route || opts.is_app_shell
